import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Icons } from '../Icons';
import { Button } from '../Button';

interface NavigationMenuItem {
  title: string;
  path: string;
  icon?: string;
}

interface NavigationSidebarProps {
  menuItems?: NavigationMenuItem[];
  isOpen: boolean;
  onToggle: () => void;
}

const defaultMenuItems: NavigationMenuItem[] = [
  {
    title: 'Studies',
    path: '/',
    icon: 'TabStudies',
  },
  {
    title: 'Templates',
    path: '/templates',
    icon: 'ListView',
  },
];

function NavigationSidebar({
  menuItems = defaultMenuItems,
  isOpen,
  onToggle,
}: NavigationSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    // Optional: close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      onToggle();
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={classNames(
          'fixed left-0 top-0 z-50 h-full bg-[#001529] transition-transform duration-300 ease-in-out',
          'flex flex-col',
          {
            'translate-x-0': isOpen,
            '-translate-x-full': !isOpen,
          }
        )}
        style={{ width: '240px' }}
      >
        {/* Header */}
        <div className="flex h-[48px] items-center justify-between border-b border-gray-700 px-4">
          <div className="flex items-center gap-2">
            <Icons.IQLineLogo className="h-6" />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="text-white hover:bg-gray-700"
          >
            <Icons.ChevronLeft className="h-5 w-5" />
          </Button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon ? Icons[item.icon as keyof typeof Icons] : null;
            const active = isActive(item.path);

            return (
              <button
                key={index}
                onClick={() => handleMenuItemClick(item.path)}
                className={classNames(
                  'flex w-full items-center gap-3 px-6 py-3 text-left transition-colors',
                  {
                    'bg-primary text-white': active,
                    'text-gray-300 hover:bg-gray-700 hover:text-white': !active,
                  }
                )}
              >
                {IconComponent && (
                  <span className="flex h-5 w-5 items-center justify-center">
                    <Icons.ByName
                      name={item.icon!}
                      className="h-5 w-5"
                    />
                  </span>
                )}
                <span className="flex-1 text-sm font-medium">{item.title}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}

export default NavigationSidebar;
